/**
 * AWS Rekognition Diagnostic Component
 * This component tests AWS Rekognition connectivity and displays diagnostic information
 */
import React, { useState } from 'react';
import { 
  Card, 
  CardHeader,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Typography,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import axiosInstance from '../api/axiosInstance';

const AwsRekognitionDiagnostic = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Function to test the AWS Rekognition connection
  const testConnection = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await axiosInstance.get('/api/face-recognition/test-connection');
      setResult(response.data);
    } catch (err) {
      console.error('Error testing AWS Rekognition connection:', err);
      setError(err.response?.data || { 
        message: 'Failed to test AWS Rekognition connection', 
        error: err.message 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 4, boxShadow: 1 }}>
      <CardHeader 
        title="AWS Rekognition Diagnostic"
        action={
          <Button 
            variant="contained"
            color="primary"
            size="small" 
            onClick={testConnection} 
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
          >
            {loading ? 'Testing...' : 'Test Connection'}
          </Button>
        }
      />
      <CardContent>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
            <CircularProgress color="primary" />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Testing AWS Rekognition connection...
            </Typography>
          </Box>
        )}
        
        {result && result.success && (
          <Alert severity="success">
            <AlertTitle>Connection Successful!</AlertTitle>
            <Typography variant="body1">The connection to AWS Rekognition was successful.</Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2"><strong>Region:</strong> {result.region}</Typography>
              <Typography variant="body2"><strong>Collections Found:</strong> {result.collections ? result.collections.length : 0}</Typography>
            </Box>
            
            {result.collections && result.collections.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2"><strong>Available Collections:</strong></Typography>
                <List dense>
                  {result.collections.map((collection, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={collection} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error">
            <AlertTitle>Connection Failed</AlertTitle>
            <Typography variant="body1">{error.message}</Typography>
            
            {error.error && (
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Error:</strong> {error.error}</Typography>
            )}
            
            {error.code && (
              <Typography variant="body2"><strong>Error Code:</strong> {error.code}</Typography>
            )}
            
            {error.region && (
              <Typography variant="body2"><strong>Region:</strong> {error.region}</Typography>
            )}
            
            {error.troubleshooting && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2"><strong>Troubleshooting Suggestions:</strong></Typography>
                <List dense>
                  {error.troubleshooting.map((suggestion, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={suggestion} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Alert>
        )}
        
        {!loading && !result && !error && (
          <Typography color="textSecondary">Click the "Test Connection" button to check AWS Rekognition connectivity.</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default AwsRekognitionDiagnostic;
